import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppConstants } from 'common/app-constants';
import { createAccountEmailInputValidator, createAccountInputValidator } from 'common/utils/createAccountValidators';
import { CreateUserDetails, ExportUserDetailsEntity, RegistrationHistory, UserCreateStrings, UserDetails, UserDetailsEntity, UserFeatures, UserRegistrationDetails, UserSecurityRoles } from 'private/app/models/user.model';
import { inputValidator } from '../account-admin-edit-form.service';
import { ContentHeadingData } from 'common/models/content-heading';
import { TranslateService } from '@ngx-translate/core';
import { ToastService, ToastTheme } from 'common/services/toast.service';
import { Paginable } from 'common/utils/PaginationUtils';
import { CountryForMasking } from '../account-admin-company-create.service';

@Injectable({ providedIn: 'root' })
export class UserAdminService {
    public userSecurityRoleGrouping: string[];
    public userSecurityAdditionalRoleGrouping: string[];
    public menuActive: boolean = false;
    public bannerType = 'UserEdit';
    public breadCrumbData: ContentHeadingData = {
        Content: {
            title: this.translate.instant('USER_ADMIN.HEADING'),
            pageTitle: this.translate.instant('USER_ADMIN.HEADING')
        },
        breadCrumbs: [
            {
                title: this.translate.instant('ACCOUNT_ADMIN.PARENT_1'),
                url: '/admin'
            },
            {
                title: `${this.translate.instant('ACCOUNT_ADMIN.PARENT_2')}`,
                url: '/Admin/Admin-Tools/account-admin.html'
            }
        ]
    };

    public userEditForm = new UntypedFormGroup({
        firstName: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        lastName: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        userID: new UntypedFormControl(''),
        userTitle: new UntypedFormControl('', [createAccountInputValidator(41, false)]),
        employerName: new UntypedFormControl(''),

        companyName: new UntypedFormControl(''),
        companyId: new UntypedFormControl(''),
        hvacPin: new UntypedFormControl(''),

        userAddress1: new UntypedFormControl('', [createAccountInputValidator(61, true)]),
        userAddress2: new UntypedFormControl('', [createAccountInputValidator(61, false)]),
        userCity: new UntypedFormControl('', [createAccountInputValidator(41, true)]),

        userState: new UntypedFormControl('', [Validators.required]),
        userCountry: new UntypedFormControl('', [Validators.required]),
        userPostalCode: new UntypedFormControl('', [Validators.required]),

        userPhone: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        userCellPhone: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        userFax: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),

        email: new UntypedFormControl('',
            [createAccountEmailInputValidator(242, false, AppConstants.validEmailRegex)]),
        contactOnly: new UntypedFormControl(false),

        lastLoginDate: new UntypedFormControl(''),
        registrationAction: new UntypedFormControl('', [Validators.required]),
        comments: new UntypedFormControl('', [createAccountInputValidator(500, false)])
    });

    constructor(
        private translate: TranslateService,
        private readonly toastService: ToastService

    ) {}

    public smoothScrollToTop() {
        window.scroll({
            behavior: 'smooth',
            top: 0,
            left: 0
        });
    }

    public transformRegHistoryToPaginableData(data: RegistrationHistory[] | undefined): Paginable<RegistrationHistory>[] {
        return (data || []).map((regHistory) => ({
            data: regHistory,
            getId: () => regHistory.id.toString()
        }));
    }

    getActiveScreenName(activeUserScreen: string, userName?: string): string {
        let activeScreenName = '';

        if (activeUserScreen === 'EditInfo') {
            activeScreenName = this.translate.instant('USER_ADMIN.EDIT_USER_DETAILS');
        }
        else if (activeUserScreen === 'EditSecurity') {
            activeScreenName = this.translate.instant('USER_ADMIN.EDIT_USER_SECURITY');
        }
        else if (activeUserScreen === 'ReadOnlyUserInfo') {
            activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_INFORMATION');
        }
        else if (activeUserScreen === 'EditRelations') {
            activeScreenName = this.translate.instant('ACCOUNT_ADMIN.EDIT_USER_RELATIONSHIP', { userName: userName || '' });
        }
        else if (activeUserScreen === 'CloneUser') {
            activeScreenName = this.translate.instant('ACCOUNT_ADMIN.CLONE_USER');
        }
        else if (activeUserScreen === 'LayerView') {
            activeScreenName = `${this.translate.instant('USER_ADMIN.LAYER_VIEW.LAYER_DATA_FOR')} ${userName}`;
        }
        else if (activeUserScreen === 'Step 4: User Roles') {
            activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_CREATE_PAGE.STEP_4');
        }

        return activeScreenName;
    }

    createUserPostBody(user: UserDetailsEntity): UserDetailsEntity {
        this.userEditForm.controls.firstName && (user.firstName = `${this.userEditForm.controls.firstName.value}`.trim());
        this.userEditForm.controls.lastName && (user.lastName = `${this.userEditForm.controls.lastName.value}`.trim());
        this.userEditForm.controls.userID && (user.userID = `${this.userEditForm.controls.userID.value}`);
        this.userEditForm.controls.userTitle && (user.userTitle = `${this.userEditForm.controls.userTitle.value}`.trim());

        this.userEditForm.controls.companyName && (user.companyName = `${this.userEditForm.controls.companyName.value}`);
        this.userEditForm.controls.companyId && (user.companyId = `${this.userEditForm.controls.companyId.value}`);
        this.userEditForm.controls.hvacPin && (user.hvacPin = `${this.userEditForm.controls.hvacPin.value}`);

        this.userEditForm.controls.userAddress1 && (user.userAddress1 = `${this.userEditForm.controls.userAddress1.value}`.trim());
        this.userEditForm.controls.userAddress2 && (user.userAddress2 = `${this.userEditForm.controls.userAddress2.value}`);
        this.userEditForm.controls.userCity && (user.userCity = `${this.userEditForm.controls.userCity.value}`.trim());
        this.userEditForm.controls.userCountry && (user.userCountry = (Array.isArray(this.userEditForm.controls.userCountry.value)) ? `${this.userEditForm.controls.userCountry.value[0].value}` : `${this.userEditForm.controls.userCountry.value.split(' - ')[1]}`);
        this.userEditForm.controls.userState && (user.userState = (Array.isArray(this.userEditForm.controls.userState.value)) ? `${this.userEditForm.controls.userState.value[0].value}` : `${this.userEditForm.controls.userState.value.split(' - ')[0]}`);

        this.userEditForm.controls.userPostalCode && (user.userPostalCode = `${this.userEditForm.controls.userPostalCode.value}`);

        this.userEditForm.controls.userPhone && (user.userPhone = `${this.userEditForm.controls.userPhone.value}`.replace(/\D/g, ''));
        this.userEditForm.controls.userCellPhone && (user.userCellPhone = `${this.userEditForm.controls.userCellPhone.value}`.replace(/\D/g, ''));
        this.userEditForm.controls.userFax && (user.userFax = `${this.userEditForm.controls.userFax.value}`.replace(/\D/g, ''));

        this.userEditForm.controls.email && (user.email = `${this.userEditForm.controls.email.value}`.trim());

        return user;
    }

    createUserRegistrationPostBody(user: UserRegistrationDetails): UserRegistrationDetails {
        this.userEditForm.controls.firstName && (user.firstName = `${this.userEditForm.controls.firstName.value}`.trim());
        this.userEditForm.controls.lastName && (user.lastName = `${this.userEditForm.controls.lastName.value}`.trim());
        this.userEditForm.controls.userID && (user.userID = `${this.userEditForm.controls.userID.value}`);
        this.userEditForm.controls.userTitle && (user.userTitle = `${this.userEditForm.controls.userTitle.value}`.trim());
        this.userEditForm.controls.employerName && (user.employerName = `${this.userEditForm.controls.employerName.value}`);
        this.userEditForm.controls.companyId && (user.companyId = `${this.userEditForm.controls.companyId.value}`);

        this.userEditForm.controls.userAddress1 && (user.userAddress1 = `${this.userEditForm.controls.userAddress1.value}`.trim());
        this.userEditForm.controls.userAddress2 && (user.userAddress2 = `${this.userEditForm.controls.userAddress2.value}`);
        this.userEditForm.controls.userCity && (user.userCity = `${this.userEditForm.controls.userCity.value}`.trim());
        this.userEditForm.controls.userCountry && (user.userCountry = (typeof this.userEditForm.value.userCountry === 'object') ? this.userEditForm.value.userCountry[0].value : this.userEditForm.value.userCountry.substring(this.userEditForm.value.userCountry.indexOf('-') + 1).trim());
        this.userEditForm.controls.userState && (user.userState = (typeof this.userEditForm.value.userState === 'object') ? this.userEditForm.value.userState[0].value : this.userEditForm.value.userState.substring(0, this.userEditForm.value.userState.indexOf('-')).trim());
        this.userEditForm.controls.userPostalCode && (user.userPostalCode = `${this.userEditForm.controls.userPostalCode.value}`);

        this.userEditForm.controls.userPhone && (user.userPhone = `${this.userEditForm.controls.userPhone.value}`.replace(/\D/g, ''));
        this.userEditForm.controls.userCellPhone && (user.userCellPhone = `${this.userEditForm.controls.userCellPhone.value}`.replace(/\D/g, ''));
        this.userEditForm.controls.userFax && (user.userFax = `${this.userEditForm.controls.userFax.value}`.replace(/\D/g, ''));

        this.userEditForm.controls.email && (user.email = `${this.userEditForm.controls.email.value}`.trim());
        this.userEditForm.controls.registrationAction && (user.registrationAction = `${this.userEditForm.controls.registrationAction.value[0].value}`);
        this.userEditForm.controls.comments && (user.registrationComment = `${this.userEditForm.controls.comments.value}`);

        return user;
    }

    toggleMenu() {
        this.menuActive = !this.menuActive;
    }

    mapCreateUserDetails() {
        const formValue = this.userEditForm.getRawValue();
        const createUserDetails: UserDetails = {
            userID: formValue.userID,
            companyId: formValue.companyId,
            employerName: formValue.employerName,
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            userTitle: formValue.userTitle,
            userAddress1: formValue.userAddress1,
            userAddress2: formValue.userAddress2,
            userCity: formValue.userCity,
            userCountry: (typeof formValue.userCountry === 'object') ? formValue.userCountry[0].value : formValue.userCountry.substring(formValue.userCountry.indexOf('-') + 1).trim(),
            userState: (typeof formValue.userState === 'object') ? formValue.userState[0].value : formValue.userState.substring(0, formValue.userState.indexOf('-')).trim(),
            userPostalCode: formValue.userPostalCode,
            userPhone: formValue.userPhone?.replace(/\D/g, '') || '',
            userCellPhone: formValue.userCellPhone?.replace(/\D/g, '') || '',
            userFax: formValue.userFax?.replace(/\D/g, '') || '',
            email: formValue.email,
            contactOnly: formValue.contactOnly !== ''
        };

        return createUserDetails;
    }

    public transformToExportUserDetailFormart(userDetails: UserDetailsEntity, userExportDetails: ExportUserDetailsEntity): ExportUserDetailsEntity {
        userExportDetails.FirstName = userDetails.firstName;
        userExportDetails.LastName = userDetails.lastName;
        userExportDetails.UserName = userDetails.userID;
        userExportDetails.HvacPin = userDetails.hvacPin;
        userExportDetails.LastLoginDate = userDetails.lastLoginDate;
        userExportDetails.EmployerName = userDetails.employerName;
        userExportDetails.CompanyId = userDetails.companyId;
        userExportDetails.AddressOne = userDetails.userAddress1;
        userExportDetails.AddressTwo = userDetails.userAddress2;
        userExportDetails.UserCity = userDetails.userCity;
        userExportDetails.UserCountry = userDetails.userCountry;
        userExportDetails.UserPostalCode = userDetails.userPostalCode;
        userExportDetails.UserState = userDetails.userState;
        userExportDetails.UserEmail = userDetails.email;
        userExportDetails.Status = userDetails.userStatus;
        userExportDetails.UserPhone = userDetails.userPhone;
        userExportDetails.UserCellPhone = userDetails.userCellPhone;
        userExportDetails.UserFax = userDetails.userFax;
        userExportDetails.Roles = (userDetails.userRolesDetails).map((data) => data['roleDisplayName']).join(', ');

        return userExportDetails;
    }

    public showToastBar(theme: ToastTheme, response: string, serviceName?: string) {
        this.toastService.add({
            content: response,
            theme: theme,
            id: serviceName || this.bannerType,
            closeable: true,
            autoClose: true
        });
    }

    public removePrefixLetterZ(str = ''): string {
        if (str && str.length > 0 && str.toLowerCase()[0] === 'z') {
            return str.substring(1);
        }

        return str;
    }

    public createUserPayloadDetails(
        userDetails: UserDetails,
        page: string,
        brandFamily: string | null,
        userRolesDetails: UserSecurityRoles[],
        additionalFeatures: UserFeatures[]
    ): CreateUserDetails {
        return {
            userID: (page === UserCreateStrings.CreateUser) ? UserCreateStrings.AdminCreate : userDetails.userID,
            companyId: userDetails.companyId.toString(),
            firstName: userDetails.firstName?.trim(),
            lastName: userDetails.lastName?.trim(),
            email: userDetails.email?.trim(),
            employerName: userDetails.employerName,
            userAddress1: userDetails.userAddress1?.trim(),
            userAddress2: userDetails.userAddress2,
            userCellPhone: userDetails.userCellPhone?.replace(/\D/g, '') || '',
            userCity: userDetails.userCity?.trim(),
            userCountry: userDetails.userCountry,
            userFax: userDetails.userFax?.replace(/\D/g, '') || '',
            userPhone: userDetails.userPhone?.replace(/\D/g, '') || '',
            userPostalCode: userDetails.userPostalCode,
            userState: userDetails.userState,
            userTitle: userDetails.userTitle?.trim(),
            userStatus: (page === UserCreateStrings.CreateUser) ? UserCreateStrings.Active : null,
            brandFamily: brandFamily,
            contactOnly: userDetails.contactOnly,
            userRolesDetails,
            additionalFeatures
        };
    }

    public updatePostalCodeValidator(form: UntypedFormGroup, countryCode: string) {
        if (countryCode === CountryForMasking.US || countryCode === CountryForMasking.USA) {
            form.controls.userPostalCode.setValidators([
                Validators.required,
                Validators.pattern(AppConstants.US_ZIP_REGEX)
            ]);
        }
        else if (countryCode === CountryForMasking.CA || countryCode === CountryForMasking.CANADA) {
            form.controls.userPostalCode.setValidators([
                Validators.required,
                Validators.pattern(AppConstants.CANADA_ZIP_REGEX)
            ]);
        }
        else {
            form.controls.userPostalCode.setValidators([
                Validators.required,
                Validators.pattern(/\S+/)
            ]);
        }
        form.controls.userPostalCode.updateValueAndValidity();
        form.controls.userPostalCode.markAsDirty();
    }


    reformatPhoneFields(form: UntypedFormGroup, controlNames: string[]) {
        controlNames.forEach((controlName) => {
            const control = form.controls[controlName];
            if (!control?.value) {
                return;
            }

            const digits = control.value.replace(/\D/g, '');
            if (!digits) {
                return;
            }
            control.setValue(digits, { emitEvent: false });

            control.updateValueAndValidity();
        });
    }
}
